import {
  DATA_VERSION_PARAM,
  DataVersion,
  UNIFIED_DATA_VERSION,
} from '@hakimo-ui/hakimo/types';

/**
 * Carries the data version into an outgoing link so the target route reads the
 * same dataset it was opened from, regardless of the opening browser's toggle
 * state. Only `v3` opts in — a v2 link stays byte-identical (no param).
 */
export function appendVersionParam(
  url: string,
  dataVersion?: DataVersion
): string {
  if (dataVersion !== UNIFIED_DATA_VERSION) {
    return url;
  }
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${DATA_VERSION_PARAM}=${UNIFIED_DATA_VERSION}`;
}

/**
 * `appendVersionParam` for callers that are already assembling a
 * `URLSearchParams` rather than a string. Same rule: only `v3` opts in, so a
 * v2 link is byte-identical to a pre-unification one.
 */
export function setDataVersionParam(
  searchParams: URLSearchParams,
  dataVersion?: DataVersion
): URLSearchParams {
  if (dataVersion === UNIFIED_DATA_VERSION) {
    searchParams.set(DATA_VERSION_PARAM, UNIFIED_DATA_VERSION);
  }
  return searchParams;
}

/**
 * Reads the data version off a route's search params. The URL is the only
 * source for deep-linkable routes -- the stored override belongs to the viewer,
 * not to the link.
 */
export function parseDataVersionParam(
  searchParams: URLSearchParams
): typeof UNIFIED_DATA_VERSION | undefined {
  return searchParams.get(DATA_VERSION_PARAM) === UNIFIED_DATA_VERSION
    ? UNIFIED_DATA_VERSION
    : undefined;
}

/**
 * A v3 location alarm id is a uuid; a v2 one is a digit string. That is the only
 * version signal available before a fetch.
 *
 * Location alarms only: raw alarms use a uuid in both versions, so the same test
 * there would force v3 on every legacy link.
 */
function looksLikeUnifiedLocationAlarmId(id?: string): boolean {
  return (
    !!id &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
  );
}

/**
 * The data version for a location-alarm deep link.
 *
 * `?version=v3` wins when present -- every link the app generates carries it.
 * A hand-typed link has nothing, and the stored override must not fill the gap,
 * so fall back to the id's own shape: a property of the link rather than of the
 * viewer. Without it a bare v3 link reads v2 and finds nothing.
 */
export function resolveLocationAlarmDataVersion(
  searchParams: URLSearchParams,
  locationAlarmId?: string
): typeof UNIFIED_DATA_VERSION | undefined {
  return (
    parseDataVersionParam(searchParams) ??
    (looksLikeUnifiedLocationAlarmId(locationAlarmId)
      ? UNIFIED_DATA_VERSION
      : undefined)
  );
}
